import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import toast from 'react-hot-toast'
import Modal from '@/components/common/Modal'
import { useProjectPermissions } from '@/auth/useProjectPermissions'
import { getFlowSource, setupFlowSource } from '@/api/integration'
import type { FlowSetupAnswer, FlowSetupInputs } from '@/api/integration'

/**
 * "Finish setting up IOFlow" — after an app is imported from git or pulled
 * onto another server, its flows are rebuilt by themselves; what only a
 * person can supply is asked HERE, once, all together:
 *
 *  - a sign-in to each application the flows use that this organization is
 *    not connected to (the form comes from IOFlow; a server address is
 *    prefilled from the source environment);
 *  - values the source does not carry (they looked like credentials);
 *  - an IOFlow administrator's code, when this IOFlow lacks an application.
 *
 * What is typed goes straight to IOFlow — Twasta stores none of it. The
 * restore then runs again and switches the waiting flows on.
 */

/** Window event: a restore was started (pull, Restore again, setup). */
export const FLOW_RESTORE_STARTED_EVENT = 'twasta:flow-restore-started'
/** Window event: open the setup dialog now (the Flows panel's button). */
export const FLOW_SETUP_OPEN_EVENT = 'twasta:flow-setup-open'

export function announceFlowRestoreStarted(project: string): void {
  window.dispatchEvent(new CustomEvent(FLOW_RESTORE_STARTED_EVENT, { detail: { project } }))
}

const POLL_MS = 3000

function lastSegment(path: string): string {
  const seg = path.split('.').pop() || path
  return seg.replace(/\[\d+\]/g, '').replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function errorMessage(e: unknown, fallback: string): string {
  const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
  return typeof detail === 'string' ? detail : fallback
}

interface DialogProps {
  project: string
  open: boolean
  inputs: FlowSetupInputs
  onClose: () => void
  onStarted: () => void
}

export function FlowSetupDialog({ project, open, inputs, onClose, onStarted }: DialogProps) {
  const [values, setValues] = useState<Record<string, Record<string, string>>>({})
  const [secrets, setSecrets] = useState<Record<string, Record<string, string>>>({})
  const [adminCode, setAdminCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [errors, setErrors] = useState<string[]>([])

  // Prefill each connection's defaults (the server address, header names).
  useEffect(() => {
    if (!open) return
    const next: Record<string, Record<string, string>> = {}
    for (const c of inputs.connections) {
      next[c.app_id] = {}
      for (const f of c.fields) if (f.default) next[c.app_id][f.name] = String(f.default)
    }
    setValues(next)
    setSecrets({})
    setAdminCode('')
    setErrors([])
  }, [open, inputs])

  const setField = (appId: string, name: string, v: string) =>
    setValues((s) => ({ ...s, [appId]: { ...(s[appId] || {}), [name]: v } }))
  const setSecret = (slug: string, path: string, v: string) =>
    setSecrets((s) => ({ ...s, [slug]: { ...(s[slug] || {}), [path]: v } }))

  const submit = async () => {
    const problems: string[] = []
    const answer: FlowSetupAnswer = { connections: [], secrets: {} }
    for (const c of inputs.connections) {
      if (c.needs_browser) continue
      const typed = values[c.app_id] || {}
      const secretTyped = c.fields.some((f) => f.secret && (typed[f.name] || '').trim())
      if (!secretTyped && c.fields.some((f) => f.secret)) continue // left for later
      const missing = c.fields.filter((f) => f.required && !(typed[f.name] || '').trim())
      if (missing.length) {
        problems.push(`${c.app_name}: ${missing.map((f) => f.label).join(', ')} required`)
        continue
      }
      answer.connections.push({ app_id: c.app_id, credentials: typed })
    }
    for (const s of inputs.secrets) {
      const typed = Object.fromEntries(
        Object.entries(secrets[s.slug] || {}).filter(([, v]) => v.trim()))
      if (Object.keys(typed).length) answer.secrets[s.slug] = typed
    }
    if (adminCode.trim()) answer.admin_code = adminCode.trim()
    setErrors(problems)
    if (problems.length) return
    if (!answer.connections.length && !Object.keys(answer.secrets).length && !answer.admin_code) {
      setErrors(['Nothing entered yet — fill in at least one section, or choose Later.'])
      return
    }
    setBusy(true)
    try {
      const res = await setupFlowSource(project, answer)
      const failed = (res.results || []).filter((r) => !r.ok)
      if (failed.length) {
        setErrors(failed.map((r) => `${r.app_name || 'IOFlow'}: ${r.error || 'not accepted'}`))
      }
      if (res.started) {
        toast.success('Setting up the flows with what you entered…')
        announceFlowRestoreStarted(project)
        onStarted()
        if (!failed.length) onClose()
      } else if (res.message) {
        toast(res.message)
      }
    } catch (e) {
      setErrors([errorMessage(e, 'Could not finish the setup')])
    } finally {
      setBusy(false)
    }
  }

  const label: CSSProperties = { display: 'block', fontSize: '0.8125rem', margin: '0.5rem 0 0.2rem' }
  const section: CSSProperties = {
    border: '1px solid var(--color-border)', borderRadius: 8, padding: '0.75rem', marginBottom: '0.75rem',
  }
  const muted: CSSProperties = { color: 'var(--color-text-secondary)', fontSize: '0.8125rem' }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Finish setting up IOFlow"
      size="wide"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>Later</button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={busy}
            data-tw="flow-setup-submit">
            {busy ? 'Setting up…' : 'Finish setup'}
          </button>
        </>
      }
    >
      <div data-tw="flow-setup">
        <p style={{ marginTop: 0 }}>
          This app’s IOFlow flows came with it and are already rebuilt here. To switch them on,
          IOFlow needs the details below. What you type goes straight to IOFlow — Twasta does not keep it.
        </p>

        {inputs.connections.length > 0 && <h4 style={{ margin: '0.75rem 0 0.5rem' }}>Sign in to applications</h4>}
        {inputs.connections.map((c) => (
          <div key={c.app_id} style={section} data-tw={`flow-setup-connection:${c.ref}`}>
            <strong>{c.app_name}</strong>
            {c.description && <div style={muted}>{c.description}</div>}
            <div style={muted}>Used by: {c.flows.join(', ')}</div>
            {c.needs_browser ? (
              <p style={{ ...muted, marginBottom: 0 }}>
                {c.app_name} signs in through its own page in the browser. Connect it in IOFlow
                (Advanced › Events & APIs › Open in IOFlow), then choose Finish setup.
              </p>
            ) : c.fields.map((f) => (
              <label key={f.name} style={label}>
                {f.label}{f.required ? ' *' : ''}
                <input
                  className="input"
                  type={f.secret ? 'password' : 'text'}
                  autoComplete={f.secret ? 'new-password' : 'off'}
                  value={values[c.app_id]?.[f.name] || ''}
                  onChange={(e) => setField(c.app_id, f.name, e.target.value)}
                  style={{ width: '100%' }}
                  data-tw={`flow-setup-field:${c.ref}:${f.name}`}
                />
              </label>
            ))}
            {c.error && <div style={{ color: 'var(--color-error)', fontSize: '0.8125rem' }}>{c.error}</div>}
          </div>
        ))}

        {inputs.secrets.length > 0 && <h4 style={{ margin: '0.75rem 0 0.5rem' }}>Values not kept in the source</h4>}
        {inputs.secrets.map((s) => (
          <div key={s.slug} style={section}>
            <strong>{s.name}</strong>
            <div style={muted}>These looked like credentials, so git does not carry them.</div>
            {s.paths.map((p) => (
              <label key={p} style={label}>
                {lastSegment(p)} <span style={muted}>({p})</span>
                <input
                  className="input"
                  type="password"
                  autoComplete="new-password"
                  value={secrets[s.slug]?.[p] || ''}
                  onChange={(e) => setSecret(s.slug, p, e.target.value)}
                  style={{ width: '100%' }}
                  data-tw={`flow-setup-secret:${s.slug}:${p}`}
                />
              </label>
            ))}
          </div>
        ))}

        {inputs.missing_apps.length > 0 && (
          <div style={section}>
            <strong>Applications this IOFlow does not have</strong>
            <ul style={{ margin: '0.35rem 0', paddingLeft: '1.2rem' }}>
              {inputs.missing_apps.map((m) => (
                <li key={m.ref}>{m.app_name} <span style={muted}>(used by {m.flows.join(', ')})</span></li>
              ))}
            </ul>
            <div style={muted}>
              Installing one needs an IOFlow administrator. Paste an administrator’s IOFlow code to
              install them now (used once, never stored), or ask an administrator to install them.
            </div>
            <label style={label}>
              IOFlow administrator code
              <input className="input" type="password" autoComplete="off" value={adminCode}
                onChange={(e) => setAdminCode(e.target.value)} style={{ width: '100%' }}
                data-tw="flow-setup-admin-code" />
            </label>
          </div>
        )}

        {errors.length > 0 && (
          <ul role="alert" style={{ color: 'var(--color-error)', margin: '0.5rem 0 0', paddingLeft: '1.2rem' }}>
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        )}
      </div>
    </Modal>
  )
}

function hasInputs(i?: FlowSetupInputs | null): i is FlowSetupInputs {
  return !!i && (i.connections.length + i.secrets.length + i.missing_apps.length) > 0
}

/**
 * Mounted once beside the Workbench. When this app's flow restore (after an
 * import or a pull) needs a person, it opens the setup dialog by itself —
 * once per restore result; "Later" waits for the next one, and the Flows
 * panel can always reopen it.
 */
export function FlowSetupPrompt({ project }: { project: string }) {
  const perms = useProjectPermissions()
  const canEdit = perms.canEditObjects
  const [inputs, setInputs] = useState<FlowSetupInputs | null>(null)
  const [open, setOpen] = useState(false)
  const [running, setRunning] = useState(false)
  const lastSeen = useRef('')
  const dismissKey = `tw-flow-setup-dismissed:${project}`

  const check = useCallback(async (force = false) => {
    if (!project || !canEdit) return
    let data
    try {
      data = await getFlowSource(project)
    } catch {
      return
    }
    const report = data.restore || {}
    const busy = report.status === 'running' || report.status === 'pending'
    setRunning(busy)
    if (busy || !report.needs_input || !hasInputs(report.inputs)) {
      if (!busy) setInputs(null)
      return
    }
    setInputs(report.inputs)
    const stamp = report.finished_at || report.started_at || ''
    let dismissed = ''
    try { dismissed = sessionStorage.getItem(dismissKey) || '' } catch { /* private mode */ }
    if (force || (stamp !== lastSeen.current && stamp !== dismissed)) {
      lastSeen.current = stamp
      setOpen(true)
    }
  }, [project, canEdit, dismissKey])

  useEffect(() => { lastSeen.current = ''; void check() }, [check])

  useEffect(() => {
    if (!running) return undefined
    const t = setTimeout(() => { void check() }, POLL_MS)
    return () => clearTimeout(t)
  }, [running, check])

  useEffect(() => {
    const started = (e: Event) => {
      const p = (e as CustomEvent<{ project?: string }>).detail?.project
      if (!p || p === project) { setRunning(true); setTimeout(() => { void check() }, POLL_MS) }
    }
    const openNow = (e: Event) => {
      const p = (e as CustomEvent<{ project?: string }>).detail?.project
      if (!p || p === project) void check(true)
    }
    window.addEventListener(FLOW_RESTORE_STARTED_EVENT, started)
    window.addEventListener(FLOW_SETUP_OPEN_EVENT, openNow)
    return () => {
      window.removeEventListener(FLOW_RESTORE_STARTED_EVENT, started)
      window.removeEventListener(FLOW_SETUP_OPEN_EVENT, openNow)
    }
  }, [project, check])

  const close = () => {
    setOpen(false)
    try { if (lastSeen.current) sessionStorage.setItem(dismissKey, lastSeen.current) } catch { /* ignore */ }
  }
  const memoInputs = useMemo(() => inputs, [inputs])
  if (!memoInputs || !canEdit) return null
  return (
    <FlowSetupDialog
      project={project}
      open={open}
      inputs={memoInputs}
      onClose={close}
      onStarted={() => setRunning(true)}
    />
  )
}
